/* eslint-disable no-param-reassign */
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { ProductPublicModels } from '@features/products/product';
import { INVIEWPORT_ID_ATTRIBUTE_NAME } from '@layout/ui/directives/inviewport.directive';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { Ga4ItemListVisible } from '@shared/analytics/GA4/store/ga4-analytics.actions';
import { ZenitListItemVisible } from '@shared/analytics/ZENIT/store/actions/zenit.actions';
import { ProductViewPlace } from '@shared/product/models';

// #region Models
interface ViewItemCallbackState {
    isFirstTime: boolean;
    productViewedGA4: ProductPublicModels.PublicProduct;
    productViewedZenit: ProductPublicModels.PublicProduct;
    timer: number;
}

interface ViewItemCallbackIntersectionData {
    entry: IntersectionObserverEntry;
    isIntersectingByConfig: boolean;
}

export interface ViewItemServiceConfig {
    delayMilisecondsUntilEvent: number; // Delay en milisegundos del tiempo que esperará antes de enviar el evento automaticamente (Para su uso fuera de la clase)
    firstTimeViewTreshold: number[]; // Umbral a partir del cual se considera que un item es visible la primera vez que se ven items (isFirstTime == true)
    isOnce: boolean; // Si es true envía lo que hay en el buffer (aunque no cumpla otras condiciones), lo vacía y luego deja de observar el elemento
    itemsBatch: number; // Tamaño máximo del buffer antes de que envie el evento
    nonExactThreshold: number[]; // Si el obsevador tiene alguna opción que afecte al cálculo del ratio de intersección se lanzarán triggers cada 10% para realizar el cálculo manual
    rootMargin?: string; // Márgenes respecto al elemento padre
    standardViewTreshold: number[]; // Umbral a partir del cual se considera que un item es visible después de la primera vez que se ven items (isFirstTime == false)
}

export interface ViewItemProduct extends ProductPublicModels.PublicProduct {
    idMedia?: string; /** Id del media que se muestra para el producto */
}

export interface ViewItemOrigin {
    eventFrom: EventFrom;
    place: ProductViewPlace;
    relatedsSectionId: string;
}

/** Propiedades de acceso desde los effects de control */
export const viewItemServiceConfig: ViewItemServiceConfig = {
    itemsBatch: 15,
    delayMilisecondsUntilEvent: 60000, // 60 segundos
    firstTimeViewTreshold: [0.2, 1], // El elemento se ve por lo menos un 20%
    standardViewTreshold: [0.50, 1], // El elemento se ve por lo menos un 50%
    nonExactThreshold: [0, 0.2, 0.4, 0.6, 0.8, 1], // Si el obsevador tiene alguna opción que afecte al cálculo del ratio de intersección se lanzarán triggers cada 20% para realizar el cálculo manual
    isOnce: false,
};
// #endregion

@Injectable()
export class ViewItemService {
    /** Propiedades de acceso desde los effects de control */
    public readonly config: ViewItemServiceConfig = viewItemServiceConfig;

    /** Estado para cada instancia */
    private readonly state = {
        isFirstTime: true,
        productViewedGA4: undefined,
        productViewedZenit: undefined,
        timer: undefined,
    };

    private static isFirstBatch = true;

    private readonly elementsViewed: Set<string> = new Set();

    private productsBuffer: ProductPublicModels.PublicProduct[] = [];

    private observer: IntersectionObserver;

    constructor(
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly platformService: CorePlatformService,
        private readonly store: Store,
    ) { }

    public getIsFirstBatch(): boolean {
        return ViewItemService.isFirstBatch;
    }

    public resetIsFirstBatch(): void {
        ViewItemService.isFirstBatch = true;
        this.elementsViewed.clear();
    }

    /*
     * Listas cuyos items se van cargando de manera asíncrona (Ej. parrillas)
     */
    public actionWhenVisibleAsync(
        product: ProductPublicModels.PublicProduct,
        viewItemOrigin: ViewItemOrigin,
        idMedia: string,
        intersectionEntry: IntersectionObserverEntry,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): void {
        if (!this.platformService.isBrowser()) return;

        const viewItemProduct = this.addDataToProduct(product, { idMedia });
        // Las options del observer pueden tener un rootMargin para funcionalidad. Aquí nos interesa el ratio de intersección del elemento.
        const realIntersectionRatio = this.calculateIntersectionRatioByTarget(this.window, intersectionEntry);

        // Instancias de IntersectionObserverEntry podrían tener isIntersecting a true debido al threshold
        // de funcionalidad, se tiene que revisar que cumple el threshold definido en la configuración.
        const isIntersectingByConfig = !!this.getThreshold(overrideConfig)
            .find((value) => value <= realIntersectionRatio);

        this.actionWhenVisibleAsyncCallback(
            this.state,
            { entry: intersectionEntry, isIntersectingByConfig },
            viewItemProduct,
            viewItemOrigin,
            false,
            overrideConfig
        );
    }

    public viewProduct(
        product: ViewItemProduct,
        inviewportElementid: string,
        isPassthrough: boolean,
        isReset?: boolean,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): ViewItemProduct[] {
        const returnProducts = this.getReturnProducts(product, isPassthrough, isReset, overrideConfig);

        const isProductViewed = this.elementsViewed.has(inviewportElementid);
        if (product && !isProductViewed) {
            this.productsBuffer.push(product);
            this.elementsViewed.add(inviewportElementid);
        }

        return returnProducts;
    }

    /**
    * Crea un observer personalizado para reaccionar a la visibilidad de elementos que no
    * tienen lógica para notificar sus cambios.
    */
    public actionWhenVisibleCustomObserver(
        element: Element,
        eventFrom: EventFrom,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ) {
        this.observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                this.actionWhenVisibleAsyncCallback(
                    null,
                    { entry, isIntersectingByConfig: true },
                    null,
                    { eventFrom, place: null, relatedsSectionId: null },
                    false,
                    overrideConfig
                );
            }
        }, { threshold: [...this.getThreshold(overrideConfig)], rootMargin: overrideConfig?.rootMargin });
        this.observer.observe(element);
    }

    public unobserve(element: Element): void {
        this.observer.unobserve(element);
    }

    /** Calculo "manual" del ratio de intersección de un elemento para los casos en los que
     * alguna opición del observer provoque que el que sea proporcionado en el callback no
     * se corresponda exactamente al ratio del elemento objetivo.
     */
    private calculateIntersectionRatioByTarget(window: CommonWindow, entry: IntersectionObserverEntry) {
        const targetRect = entry.boundingClientRect;

        const actualRootRect = {
            bottom: window.innerHeight, // coordinate
            height: window.innerHeight, // size
            left: 0, // coordinate
            right: window.innerWidth, // coordinate
            top: 0, // coordinate
            width: window.innerWidth, // size
        };

        const intersectionWidth = Math.max(
            0,
            Math.min(targetRect.right, actualRootRect.right) - Math.max(targetRect.left, actualRootRect.left)
        );
        const intersectionHeight = Math.max(
            0,
            Math.min(targetRect.bottom, actualRootRect.bottom) - Math.max(targetRect.top, actualRootRect.top)
        );
        const intersectionArea = intersectionWidth * intersectionHeight;

        const targetArea = targetRect.width * targetRect.height;

        // Calcular intersectionRatio real
        const realIntersectionRatio = targetArea > 0 ? intersectionArea / targetArea : 0;

        return realIntersectionRatio;
    }

    /** Callback con temporizador para los casos donde unas condiciones se deben cumplir
     * durante un tiempo para considerar el elemento visible.
     */
    private asyncCallbackTimeout(
        state: ViewItemCallbackState,
        entry: IntersectionObserverEntry,
        product: ViewItemProduct,
        viewItemOrigin: ViewItemOrigin,
        timeoutTime: number,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): number {
        return setTimeout(() => {
            this.actionWhenVisibleAsyncCallback(
                state,
                { entry, isIntersectingByConfig: true },
                product,
                viewItemOrigin,
                true,
                overrideConfig
            );
        }, timeoutTime) as unknown as number;
    }

    private getThreshold(overrideConfig?: Partial<ViewItemServiceConfig>): number[] {
        if (ViewItemService.isFirstBatch) return overrideConfig?.firstTimeViewTreshold || this.config.firstTimeViewTreshold;
        return overrideConfig?.standardViewTreshold || this.config.standardViewTreshold;
    }

    private handleZenitLogic(
        state: ViewItemCallbackState,
        isIntersectingByConfig: boolean,
        entry: IntersectionObserverEntry,
        product: ViewItemProduct,
        viewItemOrigin: ViewItemOrigin,
        timeoutEnd: boolean,
    ): void {
        if (!product) {
            return;
        }
        // Si el producto es visible y no se ha visto previamente registra el producto para evitar duplicación e inicia el temporizador de 1s
        if (isIntersectingByConfig && !timeoutEnd
            && state.productViewedZenit?.id !== product?.id
        ) {
            state.productViewedZenit = product;
            state.timer = this.asyncCallbackTimeout(state, entry, product, viewItemOrigin, 1000);
        }
        // Si el producto deja de ser visible y es un producto registrado limpia el temporizador y elimina los registros de este y el producto
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        if (!isIntersectingByConfig && !timeoutEnd
            && state.productViewedZenit?.id === product?.id
        ) {
            this.clearTimerOnProductNotVisible(state);
        }
        // Si ha finalizado el temporizador mientras el producto está visible lanza el evento de producto visto para Zenit
        if (timeoutEnd && isIntersectingByConfig) {
            this.zenitProductVisible(entry, viewItemOrigin, product);
        }
    }

    private clearTimerOnProductNotVisible(state: ViewItemCallbackState) {
        clearTimeout(state.timer);
        state.timer = undefined;
        state.productViewedZenit = undefined;
    }

    private zenitProductVisible(
        entry: IntersectionObserverEntry,
        viewItemOrigin: ViewItemOrigin,
        product: ViewItemProduct,
    ): void {
        const inviewportElementId = entry.target.getAttribute(INVIEWPORT_ID_ATTRIBUTE_NAME);

        this.store.dispatch(ZenitListItemVisible({
            eventFrom: viewItemOrigin.eventFrom,
            inviewportElementId,
            place: viewItemOrigin.place,
            product,
            relatedsSectionId: viewItemOrigin.relatedsSectionId,
        }));
    }

    private handleGA4Logic(
        state: ViewItemCallbackState,
        product: ViewItemProduct,
        place: ProductViewPlace,
        intersectionData: ViewItemCallbackIntersectionData,
        eventFrom: EventFrom,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): void {
        const gridLocations = ['PRODUCT_CUSTOM_CATEGORY', 'PRODUCT_NEW_COLLECTION', 'PRODUCT_GRID', 'SEARCH'];
        if (state?.isFirstTime) state.productViewedGA4 = undefined;
        /** Reutilizar primera condicion si se quiere utilizar con listas de productos, si se requirieren condiciones diferentes para otros casos crear nuevo IF */
        // Si se ha proporcionado un producto que está en un grid, es visible y no se ha visto previamente registra el producto y envia el evento de producto visto para GA4
        if (product?.id && gridLocations.includes(place)
            && intersectionData.isIntersectingByConfig
            && state?.productViewedGA4?.id !== product?.id) {
            this.ga4ProductVisible(state, product, intersectionData, eventFrom);

            // Enviar lo que hay en el buffer cuando se especifica en la overrideConfig o cuando se llega al final de un elemento espeficicado
            // (Ej. Enviar el resto de productos pendientes de enviar al llegar al final de una parrilla)
        } else if (overrideConfig?.isOnce && intersectionData.entry.boundingClientRect.bottom < window.innerHeight) {
            this.ga4PassThroughCallback(eventFrom, intersectionData);
        }
    }

    private ga4ProductVisible(
        state: ViewItemCallbackState,
        product: ViewItemProduct,
        intersectionData: ViewItemCallbackIntersectionData,
        eventFrom: EventFrom
    ): void {
        state.productViewedGA4 = product;
        state.isFirstTime = false;
        const inviewportElementId = intersectionData.entry.target.getAttribute(INVIEWPORT_ID_ATTRIBUTE_NAME);

        this.store.dispatch(Ga4ItemListVisible({
            eventFrom,
            inviewportElementId,
            product,
        }));
    }

    private ga4PassThroughCallback(eventFrom: EventFrom, intersectionData: ViewItemCallbackIntersectionData): void {
        this.store.dispatch(Ga4ItemListVisible({
            eventFrom,
            product: undefined,
            isPassthrough: true,
        }));
        if (this.observer) this.observer.unobserve(intersectionData.entry.target);
    }

    private actionWhenVisibleAsyncCallback(
        state: ViewItemCallbackState,
        intersectionData: ViewItemCallbackIntersectionData,
        product: ViewItemProduct,
        viewItemOrigin: ViewItemOrigin | null,
        timeoutEnd: boolean,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): void {
        this.handleZenitLogic(
            state,
            intersectionData.isIntersectingByConfig,
            intersectionData.entry,
            product,
            viewItemOrigin,
            timeoutEnd
        );
        this.handleGA4Logic(
            state,
            product,
            viewItemOrigin?.place,
            intersectionData,
            viewItemOrigin?.eventFrom,
            overrideConfig
        );
    }

    private getCountProductsActualInBuffer() {
        return this.productsBuffer.reduce((acc, value) => {
            if (value.gridElemType === 'BUNDLE') {
                return value.bundleProducts.length + acc;
            }
            return acc + 1;
        }, 0);
    }

    private getCountWithNewProduct(product: ProductPublicModels.PublicProduct) {
        let countWithNewProduct = this.getCountProductsActualInBuffer() ?? 0;

        if (product?.gridElemType === 'BUNDLE') {
            countWithNewProduct += product.bundleProducts.length;
        } else {
            countWithNewProduct += 1;
        }

        return countWithNewProduct;
    }

    private getReturnProducts(
        product: ProductPublicModels.PublicProduct,
        isPassthrough: boolean,
        isReset?: boolean,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): ProductPublicModels.PublicProduct[] {
        let returnProducts: ProductPublicModels.PublicProduct[] = [];

        const countWithNewProduct = this.getCountWithNewProduct(product);
        if (countWithNewProduct > (overrideConfig?.itemsBatch || this.config.itemsBatch) || isPassthrough) {
            ViewItemService.isFirstBatch = false;
            returnProducts = this.productsBuffer;
            this.productsBuffer = [];
            if (isReset) {
                this.resetIsFirstBatch();
            }
        }

        return returnProducts;
    }

    private addDataToProduct(product: ProductPublicModels.PublicProduct, data: { idMedia: string }): ViewItemProduct {
        const _product: ViewItemProduct = product;
        if (data?.idMedia) {
            return {
                ..._product,
                idMedia: data?.idMedia,
            };
        }
        return _product;
    }
}
